/**
 * Client Success sub-nav (ADR-0077). The flat tab strip across the top of the
 * Client Success component — **Surface · Firms · Users**. Flat by design:
 * Originating/Handling is a *filter toggle inside Firms*, not a third nav
 * level (ADR-0069's "no tabs-under-tabs" lesson).
 *
 * Operations uses its stat-card row as its own nav; Client Success has no such
 * row, so this strip is its navigation. Each page declares which tab is active
 * (`active` prop) rather than deriving it from the router — `/firm-success`
 * lands on Surface, and an explicit prop is simpler than reconciling that with
 * Link active-matching.
 *
 * Surface is the landing tab. It's the floor where LegalFlare staff see
 * outstanding work; pulled to the front of the strip so the default `/firm-
 * success` opens on it. The page itself is currently a placeholder while its
 * content is being designed; the tab is in front because that's where attention
 * goes, not because the page is built.
 */

import { Link } from "@tanstack/react-router";
import { cn } from "@/lib/utils";

export type ClientSuccessTab =
  | "surface"
  | "firms"
  | "firm-success"
  | "lead-volume"
  | "intake-gaps"
  | "users";

const TABS: { key: ClientSuccessTab; label: string; path: string }[] = [
  { key: "surface", label: "Surface", path: "/firm-success/surface" },
  { key: "firms", label: "Firms", path: "/firm-success/firms" },
  // Firm Success (#676) — the per-firm read surface over the metrics
  // substrate: the OF/HF lists and each firm's numbers page.
  { key: "firm-success", label: "Firm Success", path: "/firm-success/firm-success" },
  // Lead Volume (#1051, Firm Success PRD Phase 2) — LegalFlare first, then each
  // Originating Firm's full history, with the change signals.
  { key: "lead-volume", label: "Lead Volume", path: "/firm-success/lead-volume" },
  // Slice M (ADR-0100) — the chase list derived from the firms' ✓/✗ data.
  { key: "intake-gaps", label: "Intake Gaps", path: "/firm-success/intake-gaps" },
  { key: "users", label: "Users", path: "/firm-success/users" },
  // Coverage moved to Operations (2026-06-20 nav reorg) — it's routing
  // substrate, not a firm scorecard.
];

export function ClientSuccessNav({ active }: { active: ClientSuccessTab }) {
  return (
    <div
      className="mb-4 flex flex-wrap gap-1 border-b"
      role="tablist"
      data-testid="firm-success-nav"
    >
      {TABS.map((tab) => (
        <Link
          key={tab.key}
          to={tab.path}
          role="tab"
          aria-selected={tab.key === active}
          className={cn(
            "-mb-px border-b-2 px-3 py-2 text-sm transition-colors",
            tab.key === active
              ? "border-active-edge font-medium text-plane-foreground"
              : "border-transparent text-plane-muted hover:text-plane-foreground",
          )}
          data-testid={`firm-success-nav-${tab.key}`}
        >
          {tab.label}
        </Link>
      ))}
    </div>
  );
}
